import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Eye, Trash2, Package, CheckCircle, XCircle, Edit2, Send, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { grnV2API } from '@/services/api';
import api from '@/services/api';
import GRNDetailView from '@/components/grn/GRNDetailView';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_verification: { label: 'Pending Verification', color: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const GRNPage = () => {
  const navigate = useNavigate();
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const { toast } = useToast();

  const fetchGRNs = useCallback(async () => {
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const response = await grnV2API.listAll(params);
      setGRNs(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch GRNs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, toast]);

  useEffect(() => {
    fetchGRNs();
  }, [fetchGRNs]);

  const handleViewGRN = async (grn) => {
    setSelectedGRN(grn);
    setViewDialogOpen(true);
    try {
      const isLocal = grn.grn_type === 'local' || !grn.grn_type;
      const res = isLocal ? await grnV2API.getLocal(grn.id) : await grnV2API.getInternational(grn.id);
      setSelectedGRN({ ...res.data, grn_type: grn.grn_type });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load GRN details', variant: 'destructive' });
    }
  };

  const handleSubmitForVerification = async () => {
    try {
      const isLocal = selectedGRN.grn_type === 'local' || !selectedGRN.grn_type;
      if (isLocal) {
        await grnV2API.submitLocalForVerification(selectedGRN.id);
      } else {
        await grnV2API.submitInternationalForVerification(selectedGRN.id);
      }
      toast({ title: 'Success', description: 'GRN submitted for warehouse verification. Stock will update after warehouse verifies.' });
      setCompleteDialogOpen(false);
      fetchGRNs();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit for verification', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      const isLocal = selectedGRN.grn_type === 'local' || !selectedGRN.grn_type;
      if (isLocal) {
        await grnV2API.deleteLocal(selectedGRN.id);
      } else {
        await grnV2API.deleteInternational(selectedGRN.id);
      }
      toast({ title: 'Success', description: 'GRN deleted' });
      setDeleteDialogOpen(false);
      fetchGRNs();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Goods Receipt Notes</h1>
          <p className="text-gray-500">Receive and record incoming goods</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate('/hq/purchase/grn/local/new')} 
            className="border-teal-300 text-teal-600 hover:bg-teal-50" 
            data-testid="create-local-grn-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Local GRN
          </Button>
          <Button 
            onClick={() => navigate('/hq/purchase/grn/international/new')} 
            className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-200" 
            data-testid="create-international-grn-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> International GRN
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search by vendor name / code, GRN number, PO or invoice number..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 bg-white border-gray-300" data-testid="grn-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-gray-300">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : grns.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{search ? `No GRNs match "${search}"` : 'No GRNs found'}</p>
            {search ? (
              <Button onClick={() => setSearchInput('')} variant="link" className="text-teal-600 mt-2" data-testid="grn-clear-search-btn">
                Clear search
              </Button>
            ) : (
              <Button onClick={() => navigate('/hq/grn/new')} variant="link" className="text-teal-600 mt-2">
                Create your first GRN
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200">
                <TableHead className="text-gray-600">GRN Number</TableHead>
                <TableHead className="text-gray-600">PO Number</TableHead>
                <TableHead className="text-gray-600">Vendor</TableHead>
                <TableHead className="text-gray-600">Invoice</TableHead>
                <TableHead className="text-gray-600">Date</TableHead>
                <TableHead className="text-gray-600">Total</TableHead>
                <TableHead className="text-gray-600">Status</TableHead>
                <TableHead className="text-gray-600">Type</TableHead>
                <TableHead className="text-gray-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grns.map(grn => {
                const StatusIcon = statusConfig[grn.status]?.icon || FileText;
                return (
                  <TableRow key={grn.id} className="border-gray-200 hover:bg-gray-50" data-testid={`grn-row-${grn.grn_number}`}>
                    <TableCell className="font-medium text-teal-600">{grn.grn_number}</TableCell>
                    <TableCell className="text-gray-600">{grn.po_number || '-'}</TableCell>
                    <TableCell className="text-gray-700">{grn.vendor_snapshot?.vendor_name || grn.vendor_name || 'Unknown'}</TableCell>
                    <TableCell className="text-gray-600">{grn.invoice_number || '-'}</TableCell>
                    <TableCell className="text-gray-600">{formatDateOnly(grn.received_date || grn.receipt_date)}</TableCell>
                    <TableCell className="text-gray-700 font-medium">₹{Math.round(Number(grn.grand_total || 0)).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[grn.status]?.color} flex items-center gap-1 w-fit`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[grn.status]?.label}
                      </Badge>
                      {grn.has_disputes && (
                        <Badge className="bg-orange-50 text-orange-700 border border-orange-200 mt-1 w-fit text-xs" data-testid={`grn-disputed-badge-${grn.grn_number}`}>
                          Disputed ({grn.dispute_count})
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={grn.grn_type === 'international' ? 'border-blue-300 text-blue-600' : 'border-gray-300 text-gray-600'}>
                        {grn.grn_type === 'international' ? 'Intl' : 'Local'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewGRN(grn)} title="View" data-testid={`grn-view-btn-${grn.grn_number}`}>
                          <Eye className="w-4 h-4 text-gray-500" />
                        </Button>
                        {(grn.status === 'draft' || grn.status === 'pending_verification') && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(grn.grn_type === 'international' ? `/hq/purchase/grn/international/${grn.id}/edit` : `/hq/purchase/grn/local/${grn.id}/edit`)} title="Edit" data-testid={`grn-edit-btn-${grn.grn_number}`}>
                            <Edit2 className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {grn.status === 'draft' && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedGRN(grn); setCompleteDialogOpen(true); }} title="Submit for Verification" data-testid={`grn-submit-verify-btn-${grn.grn_number}`}>
                              <Send className="w-4 h-4 text-amber-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedGRN(grn); setDeleteDialogOpen(true); }} title="Delete" data-testid={`grn-delete-btn-${grn.grn_number}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {grn.status === 'pending_verification' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid={`grn-awaiting-warehouse-${grn.grn_number}`}>Awaiting Warehouse</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Goods Receipt Note</DialogTitle>
            <DialogDescription className="text-gray-500">
              Full receipt detail, pricing, approval history and adjustments for {selectedGRN?.grn_number}
            </DialogDescription>
          </DialogHeader>

          <GRNDetailView grn={selectedGRN} />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => {
                if (!selectedGRN) return;
                try {
                  const type = selectedGRN.grn_type || 'local';
                  const res = await api.get(`/grn-v2/${type}/${selectedGRN.id}/pdf`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `GRN-${selectedGRN.grn_number || selectedGRN.id}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  toast({ title: 'PDF export failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
                }
              }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="grn-download-pdf-btn"
            >
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit for Verification Confirmation */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Submit for Warehouse Verification?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will submit GRN "{selectedGRN?.grn_number}" to the warehouse for verification. 
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>GRN will move to "Pending Verification" status</li>
                <li>Warehouse staff will verify received quantities</li>
                <li>Stock will update ONLY after warehouse verifies</li>
              </ul>
              <p className="mt-2 text-amber-600 font-medium">Stock will NOT be updated until warehouse completes verification.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitForVerification} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-submit-verification-btn">
              Submit for Verification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Delete GRN?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will delete "{selectedGRN?.grn_number}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GRNPage;
